import React, { useRef, useCallback } from 'react';
import { 
  Hand, MousePointer2, RotateCcw as Rotate3D, ChevronUp, ChevronDown, 
  ChevronLeft, ChevronRight, RotateCcw, RotateCw, ZoomIn, 
  ZoomOut, Play, Pause, RotateCcw as ResetIcon
} from 'lucide-react';

const KnowledgeGraphVisualization = ({
  graphData,
  selectedEntity,
  onEntityClick,
  graphSettings,
  filterSettings,
  onFilterChange,
  onGraphSettingChange,
  rotation,
  setRotation,
  zoom,
  setZoom,
  translation,
  setTranslation,
  isAutoRotating,
  setIsAutoRotating,
  isDragging,
  setIsDragging,
  dragStart,
  setDragStart,
  t
}) => {
  const svgRef = useRef(null);

  // Enhanced 3D transformation function
  const apply3DTransform = useCallback((x, y, z = 0) => {
    const radX = (rotation.x * Math.PI) / 180;
    const radY = (rotation.y * Math.PI) / 180;
    const radZ = (rotation.z * Math.PI) / 180;

    let newX = x * Math.cos(radY) - z * Math.sin(radY);
    let newZ = x * Math.sin(radY) + z * Math.cos(radY);
    
    let newY = y * Math.cos(radX) - newZ * Math.sin(radX);
    newZ = y * Math.sin(radX) + newZ * Math.cos(radX);
    
    const finalX = newX * Math.cos(radZ) - newY * Math.sin(radZ);
    const finalY = newX * Math.sin(radZ) + newY * Math.cos(radZ);

    const perspective = 500;
    const projectedX = (finalX * perspective) / (perspective + newZ);
    const projectedY = (finalY * perspective) / (perspective + newZ);
    const scale = perspective / (perspective + newZ);

    return { 
      x: projectedX, 
      y: projectedY, 
      scale: Math.max(0.3, scale),
      depth: newZ 
    };
  }, [rotation]);

  const renderGraph = useCallback(() => {
    if (!graphData.nodes.length) return null;

    const width = 800;
    const height = 600;
    const centerX = width / 2;
    const centerY = height / 2;

    const filteredNodes = graphData.nodes.filter(node => {
      if (node.type === 'protagonist' || node.type === 'helper' || 
          node.type === 'sacrificial' || node.type === 'community') {
        return filterSettings.characters;
      }
      if (node.type === 'origin' || node.type === 'destination' || 
          node.type === 'habitat' || node.type === 'obstacle' || 
          node.type === 'dwelling' || node.type === 'setting') {
        return filterSettings.places;
      }
      return filterSettings.objects;
    });

    const filteredLinks = filterSettings.relationships ? 
      graphData.links.filter(link => 
        filteredNodes.some(n => n.id === link.source) && 
        filteredNodes.some(n => n.id === link.target)
      ) : [];

    const transformedNodes = filteredNodes.map(node => {
      const transformed = apply3DTransform(node.x, node.y, node.z || 0);
      return {
        ...node,
        transformedX: transformed.x,
        transformedY: transformed.y,
        transformedScale: transformed.scale,
        depth: transformed.depth
      };
    });

    const sortedNodes = transformedNodes.sort((a, b) => a.depth - b.depth);

    const getNodeColor = (type) => {
      switch (type) {
        case 'protagonist': return '#10b981';
        case 'helper': return '#3b82f6';
        case 'sacrificial': return '#ef4444';
        case 'community': return '#8b5cf6';
        case 'origin': return '#059669';
        case 'destination': return '#eab308';
        case 'habitat': return '#0d9488';
        case 'obstacle': return '#64748b';
        case 'dwelling': return '#0891b2';
        case 'setting': return '#065f46';
        case 'life_force': return '#fbbf24';
        case 'essence': return '#7c3aed';
        case 'legacy': return '#dc2626';
        case 'bond': return '#0ea5e9';
        case 'theme': return '#be123c';
        case 'artifact': return '#a16207';
        case 'symbol': return '#0f766e';
        default: return '#6b7280';
      }
    };

    return (
      <g transform={`translate(${centerX + translation.x}, ${centerY + translation.y}) scale(${zoom})`}>
        {/* Links */}
        {filteredLinks.map((link, index) => {
          const sourceNode = sortedNodes.find(n => n.id === link.source);
          const targetNode = sortedNodes.find(n => n.id === link.target);
          if (!sourceNode || !targetNode) return null;

          const dx = targetNode.transformedX - sourceNode.transformedX;
          const dy = targetNode.transformedY - sourceNode.transformedY;
          const distance = Math.sqrt(dx * dx + dy * dy);
          const normalizedDx = dx / distance;
          const normalizedDy = dy / distance;

          const sourceRadius = sourceNode.radius * sourceNode.transformedScale;
          const targetRadius = targetNode.radius * targetNode.transformedScale;

          const startX = sourceNode.transformedX + normalizedDx * sourceRadius;
          const startY = sourceNode.transformedY + normalizedDy * sourceRadius;
          const endX = targetNode.transformedX - normalizedDx * targetRadius;
          const endY = targetNode.transformedY - normalizedDy * targetRadius;

          const avgDepth = (sourceNode.depth + targetNode.depth) / 2;
          const opacity = Math.max(0.2, 1 - avgDepth / 200);

          return (
            <g key={`link-${index}`}>
              <line
                x1={startX}
                y1={startY}
                x2={endX}
                y2={endY}
                stroke="#cbd5e1"
                strokeWidth={link.width * 0.25 * Math.min(sourceNode.transformedScale, targetNode.transformedScale)}
                strokeOpacity={opacity * 0.6}
                markerEnd="url(#arrowhead)"
              />
              {graphSettings.showRelationshipLabels && distance > 50 && (
                <text
                  x={(startX + endX) / 2}
                  y={(startY + endY) / 2}
                  textAnchor="middle"
                  fontSize={10 * Math.min(sourceNode.transformedScale, targetNode.transformedScale)}
                  fill="#64748b"
                  fillOpacity={opacity}
                  className="pointer-events-none select-none"
                >
                  {link.type}
                </text>
              )}
            </g>
          );
        })}

        {/* Nodes */}
        {sortedNodes.map((node) => {
          const nodeRadius = node.radius * 0.5 * node.transformedScale;
          const opacity = Math.max(0.3, 1 - node.depth / 200);
          
          return (
            <g key={node.id} className="cursor-pointer">
              <circle
                cx={node.transformedX}
                cy={node.transformedY}
                r={nodeRadius}
                fill={getNodeColor(node.type)}
                fillOpacity={opacity}
                stroke={selectedEntity?.id === node.id ? '#1d4ed8' : '#ffffff'}
                strokeWidth={(selectedEntity?.id === node.id ? 3 : 2) * 0.5 * node.transformedScale}
                className="hover:stroke-blue-500 transition-all duration-200"
                onClick={() => onEntityClick(node)}
                style={{
                  filter: node.depth > 0 ? 'brightness(0.8)' : 'brightness(1.2)'
                }}
              />
              {graphSettings.showLabels && nodeRadius > 5 && (
                <text
                  x={node.transformedX}
                  y={node.transformedY + nodeRadius + 15 * node.transformedScale}
                  textAnchor="middle"
                  fontSize={12 * node.transformedScale}
                  fill="#374151"
                  fillOpacity={opacity}
                  className="pointer-events-none select-none font-medium"
                >
                  {node.name}
                </text>
              )}
            </g>
          );
        })}

        <defs>
          <marker
            id="arrowhead"
            markerWidth="10"
            markerHeight="7"
            refX="9"
            refY="3.5"
            orient="auto"
          >
            <polygon
              points="0 0, 10 3.5, 0 7"
              fill="#cbd5e1"
            />
          </marker>
        </defs>
      </g>
    );
  }, [graphData, selectedEntity, onEntityClick, graphSettings, filterSettings, apply3DTransform, translation, zoom]);

  const handleMouseDown = (e) => {
    setIsDragging(true);
    setDragStart({ x: e.clientX, y: e.clientY });
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      const deltaX = e.clientX - dragStart.x;
      const deltaY = e.clientY - dragStart.y;
      setTranslation(prev => ({
        x: prev.x + deltaX * 0.5,
        y: prev.y + deltaY * 0.5
      }));
      setDragStart({ x: e.clientX, y: e.clientY });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const resetView = () => {
    setZoom(1);
    setRotation({ x: 0, y: 0, z: 0 });
    setTranslation({ x: 0, y: 0 });
    setIsAutoRotating(false);
  };

  const rotateGraph = (axis, degrees) => {
    setRotation(prev => ({
      ...prev,
      [axis]: (prev[axis] + degrees) % 360
    }));
  };

  return (
    <div className="relative border border-gray-200 rounded-lg overflow-hidden bg-gradient-to-br from-red-50 to-orange-100">
      {/* Top-Left: Filter Controls Panel */}
      <div className="absolute top-4 left-4 z-20 bg-white/95 backdrop-blur-sm rounded-lg shadow-lg p-3 border border-gray-200">
        <h4 className="font-semibold text-sm text-gray-900 mb-2">搜尋與篩選</h4>
                 <div className="space-y-2">
           <label className="flex items-center text-xs">
             <input
               type="checkbox"
               checked={filterSettings.characters}
               onChange={(e) => onFilterChange('characters', e.target.checked)}
               className="rounded border-gray-300 text-red-600 focus:ring-red-500 w-3 h-3"
             />
             <span className="ml-1 text-gray-700">{t.characters}</span>
           </label>
           <label className="flex items-center text-xs">
             <input
               type="checkbox"
               checked={filterSettings.places}
               onChange={(e) => onFilterChange('places', e.target.checked)}
               className="rounded border-gray-300 text-red-600 focus:ring-red-500 w-3 h-3"
             />
             <span className="ml-1 text-gray-700">{t.places}</span>
           </label>
           <label className="flex items-center text-xs">
             <input
               type="checkbox"
               checked={filterSettings.objects}
               onChange={(e) => onFilterChange('objects', e.target.checked)}
               className="rounded border-gray-300 text-red-600 focus:ring-red-500 w-3 h-3"
             />
             <span className="ml-1 text-gray-700">{t.objects}</span>
           </label>
           <label className="flex items-center text-xs">
             <input
               type="checkbox"
               checked={filterSettings.relationships}
               onChange={(e) => onFilterChange('relationships', e.target.checked)}
               className="rounded border-gray-300 text-red-600 focus:ring-red-500 w-3 h-3"
             />
             <span className="ml-1 text-gray-700">{t.relationships}</span>
           </label>
         </div>
      </div>

      {/* Bottom-Left: View Controls Panel */}
      <div className="absolute bottom-4 left-4 z-20 bg-white/95 backdrop-blur-sm rounded-lg shadow-lg p-3 border border-gray-200">
        <h4 className="font-semibold text-sm text-gray-900 mb-2">檢視控制</h4>
        
        {/* Auto Rotate Toggle */}
        <button
          onClick={() => setIsAutoRotating(!isAutoRotating)}
          className={`w-full flex items-center justify-center gap-1 px-2 py-1 rounded text-xs transition-colors mb-2 ${
            isAutoRotating 
              ? 'bg-red-600 text-white' 
              : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
          }`}
        >
          {isAutoRotating ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3" />}
          {isAutoRotating ? '停止自動旋轉' : '自動旋轉'}
        </button>

        {/* Zoom Controls */}
        <div className="flex items-center gap-1 mb-2">
          <button
            onClick={() => setZoom(prev => Math.min(prev + 0.2, 3))}
            className="p-1 bg-gray-100 hover:bg-gray-200 rounded transition-colors"
          >
            <ZoomIn className="w-3 h-3" />
          </button>
          <span className="text-xs text-gray-600 flex-1 text-center min-w-12">
            {Math.round(zoom / 1.5 * 100)}%
          </span>
          <button
            onClick={() => setZoom(prev => Math.max(prev - 0.2, 0.2))}
            className="p-1 bg-gray-100 hover:bg-gray-200 rounded transition-colors"
          >
            <ZoomOut className="w-3 h-3" />
          </button>
        </div>

        {/* Reset View */}
        <button
          onClick={resetView}
          className="w-full flex items-center justify-center gap-1 px-2 py-1 bg-red-50 hover:bg-red-100 text-red-700 rounded transition-colors text-xs"
        >
          <ResetIcon className="w-3 h-3" />
          重設全部
        </button>

                 {/* Display Options */}
         <div className="space-y-1 mt-2">
           <label className="flex items-center text-xs">
             <input
               type="checkbox"
               checked={graphSettings.showLabels}
               onChange={(e) => onGraphSettingChange('showLabels', e.target.checked)}
               className="rounded border-gray-300 text-red-600 focus:ring-red-500 w-3 h-3"
             />
             <span className="ml-1 text-gray-700">{t.showLabels}</span>
           </label>
           <label className="flex items-center text-xs">
             <input
               type="checkbox"
               checked={graphSettings.showRelationshipLabels}
               onChange={(e) => onGraphSettingChange('showRelationshipLabels', e.target.checked)}
               className="rounded border-gray-300 text-red-600 focus:ring-red-500 w-3 h-3"
             />
             <span className="ml-1 text-gray-700">{t.relationshipLabels}</span>
           </label>
         </div>
      </div>

      {/* Rotation Arrow Controls */}
      <div className="absolute top-2 left-1/2 transform -translate-x-1/2 z-10">
        <button
          onClick={() => rotateGraph('x', -15)}
          className="p-2 bg-white/90 hover:bg-white shadow-md rounded-lg transition-all duration-200 hover:scale-110"
          title="向上旋轉 (X軸 -15°)"
        >
          <ChevronUp className="w-5 h-5 text-gray-700" />
        </button>
      </div>

      <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 z-10">
        <button
          onClick={() => rotateGraph('x', 15)}
          className="p-2 bg-white/90 hover:bg-white shadow-md rounded-lg transition-all duration-200 hover:scale-110"
          title="向下旋轉 (X軸 +15°)"
        >
          <ChevronDown className="w-5 h-5 text-gray-700" />
        </button>
      </div>

      <div className="absolute left-2 top-1/2 transform -translate-y-1/2 z-10">
        <button
          onClick={() => rotateGraph('y', -15)}
          className="p-2 bg-white/90 hover:bg-white shadow-md rounded-lg transition-all duration-200 hover:scale-110"
          title="向左旋轉 (Y軸 -15°)"
        >
          <ChevronLeft className="w-5 h-5 text-gray-700" />
        </button>
      </div>

      <div className="absolute right-2 top-1/2 transform -translate-y-1/2 z-10">
        <button
          onClick={() => rotateGraph('y', 15)}
          className="p-2 bg-white/90 hover:bg-white shadow-md rounded-lg transition-all duration-200 hover:scale-110"
          title="向右旋轉 (Y軸 +15°)"
        >
          <ChevronRight className="w-5 h-5 text-gray-700" />
        </button>
      </div>

      <div className="absolute top-2 right-2 z-10 flex gap-1">
        <button
          onClick={() => rotateGraph('z', -15)}
          className="p-2 bg-white/90 hover:bg-white shadow-md rounded-lg transition-all duration-200 hover:scale-110"
          title="逆時針旋轉 (Z軸 -15°)"
        >
          <RotateCcw className="w-4 h-4 text-gray-700" />
        </button>
        <button
          onClick={() => rotateGraph('z', 15)}
          className="p-2 bg-white/90 hover:bg-white shadow-md rounded-lg transition-all duration-200 hover:scale-110"
          title="順時針旋轉 (Z軸 +15°)"
        >
          <RotateCw className="w-4 h-4 text-gray-700" />
        </button>
      </div>

      {/* SVG Canvas */}
      <svg
        ref={svgRef}
        width="100%"
        height="600"
        viewBox="0 0 800 600"
        className="cursor-grab active:cursor-grabbing"
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onWheel={(e) => {
          e.preventDefault();
          const delta = e.deltaY > 0 ? -0.1 : 0.1;
          setZoom(prev => Math.max(0.3, Math.min(4.5, prev + delta)));
        }}
      >
        {renderGraph()}
      </svg>

      {/* Rotation Status Display */}
      <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 bg-white/90 px-3 py-1 rounded-lg text-xs text-gray-600">
        <span>X: {rotation.x}°</span>
        <span className="mx-2">Y: {rotation.y}°</span>
        <span>Z: {rotation.z}°</span>
        <span className="ml-2">縮放: {Math.round(zoom / 1.5 * 100)}%</span>
        {isAutoRotating && (
          <span className="ml-2 text-red-600">
            <Rotate3D className="w-3 h-3 inline animate-spin mr-1" />
            自動旋轉中
          </span>
        )}
      </div>
    </div>
  );
};

export default KnowledgeGraphVisualization; 